import * as React from 'react';
import { useAppSelector, useAppSelector as useSelector } from 'Store/index';
import {
  calculateDaysDifference,
  formatTime,
  isPartialDate,
  nowPartialDate,
  parsePatientDemographics,
  partialDateFromDate,
  partialDateToValue,
  sortPartialDate,
} from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { MyServiceContext } from '..';
import { StyledTextArea } from 'Components/InputHandler/components/TextArea';
import { informantSurveysByPlatform, surveysByPlatform } from '../config';
import { equals } from 'ramda';
import colors from '../../../../config/theme/colors';
import { ININMTTreatmentPeriod } from 'neuro-schemas';
import { DateTime } from 'luxon';
import StyledDateRange from './components/StyledDateRange';
import LeftRightControls from './components/LeftRightControls';
import { styled } from '@mui/system';
import ActionButtonRounded from 'Components/ActionButtonRounded';
import phone from 'phone';
import { validate } from '@stellarq/email';
import { formatTaskActionType } from 'Routes/MyService/util';
import { fetchMyUserContactInfo, fetchMyUserDataBySsn } from 'Store/myapp/fetchers';
import TextField from 'Components/InputHandler/components/TextField';
import { Container, Item } from 'Components/Grid';

export type TaskListFormProps = {
  [platform in Platform]?: TaskStimulusFormSettings;
};

const dayMillis = 86400000;

const StyledButton = styled('a')({
  fontSize: '1.6rem',
  fontWeight: 600,
  cursor: 'pointer',
  color: colors.primary,
});

const RelativeAddingRow = ({ title, children }: { title: string; children: React.JSX.Element }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '2rem' }}>
      <div style={{ color: colors.tertiaryText }}>{title}</div>
      {children}
    </div>
  );
};

/**
 * Questionnaires that MUST be ordered by a doctor before they can be included
 * in the patient sendable notification.
 *
 * @requirement NEUR-2679
 */
const orderRequiredQuestionnaires = ['gad-7', 'bai'];

const initialTempInformantData: {
  ssn?: string;
  lastName?: string;
  firstName?: string;
  phoneNumber?: string;
  email?: string;
} = {
  ssn: undefined,
  lastName: undefined,
  firstName: undefined,
  phoneNumber: undefined,
  email: undefined,
};

const getSurveyOpts = (platform: Platform, informant: boolean) => {
  if (informant) {
    return informantSurveysByPlatform[platform] ?? [];
  }
  return (surveysByPlatform[platform] ?? [])
    ?.flatMap(s => (s === 'pedsql' ? ['pedsqlNeuromuscular', 'pedsqlFatigue'] : s))
    .filter(surv =>
      platform === 'parkinson'
        ? surv !== 'mdsUpdrsIV'
        : platform === 'ninmt'
          ? // Treatment efficiency tasks can only be sent from RTMS/TDCS forms
            surv !== 'treatmentEfficiency'
          : surv,
    );
};

const TaskListForm = ({
  taskListSettings,
  locale,
  inviteContactInfo,
  setInviteContactInfo,
  onChangeInviteContactInfo,
}: ITaskListProps) => {
  const myServContext = React.useContext(MyServiceContext);
  const { fm, editing, setEditingData, platform } = myServContext;
  const reduxDocs = useSelector((s: IState) => s.documents.sortedAndMergedDocuments);
  const latestContraIndicationDocument: Partial<IContraIndicationToTreatment> = (reduxDocs || [])
    .filter(d => d._type === 'contraIndicationsToTreatment')
    .sort((a, b) => {
      const dateb = isPartialDate(b.date) ? b.date : undefined;
      const dateA = isPartialDate(a.date) ? a.date : undefined;
      return sortPartialDate(dateb, dateA);
    })[0];
  const appointments =
    useSelector((s: IState) => s.appointments)?.appointments?.filter(appo => {
      const appointmentTimeStamp = DateTime.fromMillis(partialDateToValue(appo.startDate))
        .plus({
          hours: appo.startTime?.[0] ?? 0,
        })
        .plus({ minutes: appo.startTime?.[1] ?? 0 })
        .toMillis();
      return appointmentTimeStamp > Date.now();
    }) ?? [];
  const patientInformants = (useSelector((state: IState) => state.patient.informants) || []).filter(cr => !cr.removed);
  const patientMySQStatus = useSelector((s: IState) => s.myapp.mysqUserId);
  const sentInvites = useSelector((s: IState) => s.myapp.sentInvites);
  const patientContactInfo = useSelector((s: { patient: IPatientStore }) => s.patient.contactInfo);

  const platformSettings = platform ? taskListSettings?.[platform] : undefined;
  const [selectedPlatform, setSelectedPlatform] = React.useState<Platform | undefined>(platform);
  const [addingNewInformant, setAddingNewInformant] = React.useState<boolean>(false);
  const [validFields, setValidFields] = React.useState<{ ssn: boolean; phone: boolean; email: boolean }>({
    ssn: true,
    phone: true,
    email: true,
  });
  const [tempInformantData, setTempInformantData] = React.useState(initialTempInformantData);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [mysqUserDataFound, setMysqUserDataFound] = React.useState<boolean>(false);
  const isMountRender = React.useRef<boolean>(true);
  const isMountRender2 = React.useRef<boolean>(true);
  const isMountRender3 = React.useRef<boolean>(true);
  const isMountRender4 = React.useRef<boolean>(true);
  const isMountRender5 = React.useRef<boolean>(true);

  const taskLists = useSelector((state: IState) => state.myapp?.taskLists);
  const platformSpecificTasklists = taskLists?.filter(tl =>
    tl.tasks?.every(t => platform && surveysByPlatform[platform]?.includes(t)),
  );
  const currentRecurringPlatformSpecificTasklists = platformSpecificTasklists?.filter(
    tl => tl.recurring && (!tl.deadline || partialDateToValue(tl?.deadline) > partialDateToValue(nowPartialDate())),
  );

  const taskListData = editing?.data as unknown as ITaskList;

  const orderedQuestionnaires = reduxDocs?.find(d => d._type === 'doctorsOrdersAdditional')?.additionalDocuments ?? [];

  const tdcsAndRtmsDocs = reduxDocs
    ?.filter(d => d._type === 'rtms' || d._type === 'tdcs')
    ?.sort((a, b) => {
      const dateA = isPartialDate(a.date) ? a.date : undefined;
      const dateB = isPartialDate(b.date) ? b.date : undefined;
      return sortPartialDate(dateA, dateB);
    }) as Array<IRTMS | ITDCS>;

  const treatmentPeriodDocs = (reduxDocs || []).filter(
    d => d._type === 'ninmtTreatmentPeriod',
  ) as ININMTTreatmentPeriod[];

  const latestTreatmentPeriod = treatmentPeriodDocs?.sort((a, b) => sortPartialDate(b?.date, a?.date))[0];
  const latestTreatmentPeriodDate = latestTreatmentPeriod?.date;

  const activeTdcsOrRtms = tdcsAndRtmsDocs?.find(
    d => partialDateToValue(d?.date) >= partialDateToValue(latestTreatmentPeriodDate) && !d?.hasEnded,
  );

  const disabledOptions =
    platform && surveysByPlatform[platform]
      ? surveysByPlatform[platform]?.filter(
          q =>
            (orderRequiredQuestionnaires.includes(q) && !(orderedQuestionnaires as string[]).includes(q)) ||
            (q === 'contraIndicationsInquiry' && !activeTdcsOrRtms),
        )
      : undefined;

  const recurringDisabledOpts = currentRecurringPlatformSpecificTasklists
    ?.filter(tl => tl.id !== taskListData?.id)
    ?.flatMap(tl => tl.tasks);

  const onChangeTaskForm = (values: TOnChangeValues | Array<TOnChangeValues>): void => {
    if (Array.isArray(values)) {
      const changes = values.map(v => {
        const field = Object.keys(v)[0];
        const value = Object.values(v)[0];
        return { [field]: value };
      });
      setEditingData?.(Object.assign({}, taskListData, ...changes));
    } else {
      const field = Object.keys(values)[0];
      const value = Object.values(values)[0];
      if (field === 'title' && value && typeof value === 'string') {
        const isIromTitle = platformSettings?.[value].taskKind === 'IROM';
        if (isIromTitle) {
          setInviteContactInfo(undefined);
        } else {
          if (sentInvites.length === 0 && !firstInviteUpcoming && !patientMySQStatus)
            setInviteContactInfo(patientContactInfo?.contacts ?? []);
        }
      }
      setEditingData?.(Object.assign({}, taskListData, { [field]: value }));
    }
  };

  const handleMysqUserDataFetching = async (ssn: string, ssnLocalization: string) => {
    setLoading(true);
    const userData = await fetchMyUserDataBySsn(ssn, ssnLocalization);
    if (!userData) {
      setLoading(false);
      return;
    }
    setMysqUserDataFound(true);
    const contacts = await fetchMyUserContactInfo(userData.userId);
    const email = contacts?.find(c => c.contactType === 'email')?.contactValue?.[0];
    const phone = contacts?.find(c => c.contactType === 'sms')?.contactValue?.[0];
    const firstName = userData?.firstNames;
    const lastName = userData?.lastNames;
    setTempInformantData(prev => ({
      ssn: prev.ssn,
      lastName: lastName,
      firstName: firstName,
      phoneNumber: phone,
      email: email,
    }));
    setLoading(false);
  };

  // When title changed:
  // - select tasks that are included in that titles taskSettings
  // - set description of that title based on taskSettings
  // - set sendDate based on 'sendDateDaysBeforeDeadline' field on taskSettings
  // - if recurring and recurringDeadline, taskSettngs 'recurringTaskDeadlineDefaultDays' sets deadline
  // - if taskSettings with selected title has 'recurring' field true, set recurring true AND vice versa
  // - if taskSettings with selected title has 'recurringInterval' field, set interval, if not set undefined
  React.useEffect(() => {
    if (isMountRender.current) {
      isMountRender.current = false;
      return;
    }
    if (!platform) return;
    const changeableFieldsValues: Array<TOnChangeValues> = [];
    if (taskListData?.title) {
      const titleSelectedTasks = platformSettings?.[taskListData.title]?.defaultSelectQuestionnaires;

      let selectableTasks: string[] = [];

      for (const taskId of titleSelectedTasks || []) {
        // Custom mapping for 1:n tasktypes
        if ((platform === 'sma' || platform === 'dmd') && taskId === 'pedsql') {
          selectableTasks = [...selectableTasks, 'pedsqlNeuromuscular', 'pedsqlFatigue'];
          continue;
        }
        if (
          surveysByPlatform[platform]?.includes(taskId) &&
          !disabledOptions?.includes(taskId) &&
          (taskListData?.recurring ? !(recurringDisabledOpts as string[])?.includes(taskId) : true)
        ) {
          selectableTasks.push(taskId);
          continue;
        }
      }
      const newDescription = locale
        ? (platformSettings?.[taskListData.title]?.descriptionLocalized?.[locale] ??
          platformSettings?.[taskListData.title]?.descriptionLocalized?.['fi'])
        : undefined;
      if (!equals(titleSelectedTasks, taskListData?.tasks)) {
        changeableFieldsValues.push({ tasks: selectableTasks });
      }
      if (newDescription !== taskListData?.description) {
        changeableFieldsValues.push({ description: newDescription ?? '' });
      }
      if (taskListData?.deadline || taskListData?.recurringTaskDeadline) {
        const deadlineToValue = partialDateToValue(taskListData?.recurringTaskDeadline || taskListData?.deadline);
        let sendingDate =
          deadlineToValue - dayMillis * (platformSettings?.[taskListData?.title]?.sendDateDaysBeforeDeadline ?? 1);
        if (sendingDate < partialDateToValue(nowPartialDate())) {
          sendingDate = partialDateToValue(nowPartialDate());
        }
        if (sendingDate !== partialDateToValue(taskListData?.sendDate)) {
          changeableFieldsValues.push({ sendDate: partialDateFromDate(new Date(sendingDate)) });
        }
      }
      if (
        taskListData?.recurring &&
        taskListData?.recurringTaskDeadline &&
        platformSettings?.[taskListData.title]?.recurringTaskDeadlineDefaultDays
      ) {
        const defaultRecurringEndDate = platformSettings?.[taskListData.title]?.recurringTaskDeadlineDefaultDays;
        changeableFieldsValues.push({
          deadline: partialDateFromDate(
            new Date(
              partialDateToValue(taskListData?.recurringTaskDeadline) + dayMillis * (defaultRecurringEndDate ?? 1),
            ),
          ),
        });
      }
      if (platformSettings?.[taskListData.title]?.recurring && !taskListData?.recurring) {
        changeableFieldsValues.push({
          recurring: true,
        });
      }
      if (!platformSettings?.[taskListData.title]?.recurring && taskListData?.recurring) {
        changeableFieldsValues.push({
          recurring: false,
        });
      }
      if (
        platformSettings?.[taskListData.title]?.recurringInterval &&
        ['weekly', 'everyOtherWeek', 'everyFourthWeek'].includes(
          platformSettings[taskListData.title].recurringInterval ?? '',
        ) &&
        taskListData?.weeklySurveyInterval !== platformSettings?.[taskListData.title]?.recurringInterval
      ) {
        changeableFieldsValues.push({
          weeklySurveyInterval: platformSettings?.[taskListData.title]?.recurringInterval,
        });
      }
      if (!platformSettings?.[taskListData.title]?.recurringInterval && taskListData?.weeklySurveyInterval) {
        changeableFieldsValues.push({
          weeklySurveyInterval: undefined,
        });
      }
    }
    if (changeableFieldsValues.length > 0) {
      onChangeTaskForm(changeableFieldsValues);
    }
  }, [taskListData?.title]);

  // When deadline changed, set sendDate based on orgSettings and not earlier than now
  // And if latest contraIndicationsInq is >30 days ago, auto-select it
  React.useEffect(() => {
    if (isMountRender2.current) {
      isMountRender2.current = false;
      return;
    }
    if (taskListData?.deadline && !taskListData?.recurring) {
      const changeableFieldsValues: Array<TOnChangeValues> = [];
      const taskSettingsSendDateBeforeDeadline = taskListData?.title
        ? (platformSettings?.[taskListData?.title]?.sendDateDaysBeforeDeadline ?? 1)
        : undefined;
      if (taskSettingsSendDateBeforeDeadline) {
        const deadlineValue = partialDateToValue(taskListData?.deadline);
        const newSendDateValue = deadlineValue - dayMillis * taskSettingsSendDateBeforeDeadline;
        let newSendDate: PartialDate;
        if (newSendDateValue < partialDateToValue(nowPartialDate())) {
          newSendDate = nowPartialDate();
        } else {
          newSendDate = partialDateFromDate(new Date(newSendDateValue));
        }
        changeableFieldsValues.push({ sendDate: newSendDate });
      }
      if (
        (calculateDaysDifference(latestContraIndicationDocument?.date, taskListData?.deadline) ?? 1) > 30 &&
        platform &&
        surveysByPlatform[platform]?.includes('contraIndicationsInquiry') &&
        !taskListData?.tasks?.includes('contraIndicationsInquiry') &&
        !disabledOptions?.includes('contraIndicationsInquiry')
      ) {
        changeableFieldsValues.push({ tasks: [...(taskListData?.tasks ?? []), 'contraIndicationsInquiry'] });
      }
      if (changeableFieldsValues.length > 0) {
        onChangeTaskForm(changeableFieldsValues);
      }
    }
  }, [taskListData?.deadline]);

  // When recurring and changing recurringTaskDeadline, set sendDate based on that and no earlier than now
  // and set deadline to recurringTaskDeadline + (taskSettings 'recurringTaskDeadlineDefaultDays' * dayMillis)
  React.useEffect(() => {
    if (isMountRender3.current) {
      isMountRender3.current = false;
      return;
    }
    if (taskListData?.recurring && taskListData?.recurringTaskDeadline) {
      const changeableFieldsValues: Array<TOnChangeValues> = [];
      const taskSettingsSendDateBeforeDeadline = taskListData?.title
        ? (platformSettings?.[taskListData?.title]?.sendDateDaysBeforeDeadline ?? 1)
        : undefined;
      if (taskSettingsSendDateBeforeDeadline) {
        const recurringDeadlineValue = partialDateToValue(taskListData?.recurringTaskDeadline);
        const newSendDateValue = recurringDeadlineValue - dayMillis * taskSettingsSendDateBeforeDeadline;
        let newSendDate: PartialDate;
        if (newSendDateValue < partialDateToValue(nowPartialDate())) {
          newSendDate = nowPartialDate();
        } else {
          newSendDate = partialDateFromDate(new Date(newSendDateValue));
        }
        changeableFieldsValues.push({ sendDate: newSendDate });
        // onChangeTaskForm({ sendDate: newSendDate });
      }
      if (taskListData?.title && platformSettings?.[taskListData.title]?.recurringTaskDeadlineDefaultDays) {
        changeableFieldsValues.push({
          deadline: partialDateFromDate(
            new Date(
              partialDateToValue(taskListData?.recurringTaskDeadline) +
                dayMillis * (platformSettings?.[taskListData.title]?.recurringTaskDeadlineDefaultDays ?? 1),
            ),
          ),
        });
      }
      if (changeableFieldsValues.length > 0) {
        onChangeTaskForm(changeableFieldsValues);
      }
    }
  }, [taskListData?.recurringTaskDeadline]);

  // Empty title, tasks and description fields if platform is changed
  React.useEffect(() => {
    if (platform !== selectedPlatform) {
      onChangeTaskForm([{ tasks: undefined }, { description: '' }, { title: undefined }]);
      setSelectedPlatform(platform);
    }
  }, [platform]);

  React.useEffect(() => {
    if (isMountRender4.current) {
      isMountRender4.current = false;
      return;
    }
    if (
      taskListData?.tasks?.includes('ninmtPreInquiry') &&
      (taskListData?.recurringTaskDeadline || taskListData?.deadline)
    ) {
      const deadlineToValue = partialDateToValue(taskListData?.recurringTaskDeadline || taskListData?.deadline);
      const currentSendDateValue = taskListData?.sendDate ? partialDateToValue(taskListData?.sendDate) : undefined;
      let sendingDate = deadlineToValue - dayMillis * 14;
      if (sendingDate < partialDateToValue(nowPartialDate())) {
        sendingDate = partialDateToValue(nowPartialDate());
      } else if (currentSendDateValue && sendingDate > currentSendDateValue) {
        sendingDate = currentSendDateValue;
      }
      onChangeTaskForm({ sendDate: partialDateFromDate(new Date(sendingDate)) });
    }
  }, [taskListData?.tasks]);

  // When selecting recurring, uncheck tasks which are already in some recurring stimulus
  // Reset recurring fields if recurring ticked off
  React.useEffect(() => {
    if (isMountRender5.current) {
      isMountRender5.current = false;
      return;
    }
    const changeableFieldsValues: Array<TOnChangeValues> = [];
    if (taskListData?.recurring && recurringDisabledOpts && recurringDisabledOpts.length > 0) {
      if (isMountRender.current || taskListData?.id) {
        isMountRender.current = false;
        return;
      }
      changeableFieldsValues.push({ tasks: taskListData?.tasks?.filter(t => !recurringDisabledOpts.includes(t)) });
    }
    // Reset these fields everytime recurring checkbox is changed
    changeableFieldsValues.push({ recurringTaskDeadline: undefined });
    changeableFieldsValues.push({ weeklySurveyInterval: undefined });
    if (!taskListData?.appointmentId) {
      // Do not reset deadlien if the triggerer was appointmentId linking
      changeableFieldsValues.push({ deadline: undefined });
    }
    // Hack to nullify deadline between undefined and null as PartialDate component does not update values if
    // editing is false. And it is false when ticking recurring true.
    if (taskListData.deadline === undefined) {
      changeableFieldsValues.push({ sendDate: null });
    } else {
      changeableFieldsValues.push({ sendDate: undefined });
    }
    onChangeTaskForm(changeableFieldsValues);
  }, [taskListData?.recurring]);

  const endTimeDate = taskListData?.end_time ? new Date(taskListData.end_time) : undefined;
  const showDeadlineTime =
    taskListData?.appointmentId &&
    appointments.find(appo => appo.id === taskListData.appointmentId)?.startTime &&
    endTimeDate &&
    !(endTimeDate.getHours() === 0 && endTimeDate.getMinutes() === 0 && endTimeDate.getSeconds() === 0);

  const showInformantFields = !!(taskListData?.title && platformSettings?.[taskListData.title].taskKind === 'IROM');

  const firstInviteUpcoming =
    sentInvites.length === 0 &&
    taskLists.filter(tl => tl.task_kind === 'PROM' || (!tl.task_kind && !tl.informantId)).length > 0;

  const visibleTitlesByKind = (taskKind: 'PROM' | 'IROM') =>
    Object.keys(platformSettings ?? {}).filter(
      key => !platformSettings?.[key]?.hidden && platformSettings?.[key]?.taskKind === taskKind,
    );

  const titleOptions = [
    { title: fm('myService.forPatient'), values: visibleTitlesByKind('PROM') },
    { title: fm('myService.forRelative'), values: visibleTitlesByKind('IROM') },
  ].filter(category => category.values.length > 0);

  const orgSettings = useAppSelector(s => s.settings.orgSettings);
  const allowedPatientCountries = orgSettings.allowedPatientCountries;
  const defaultOrgSSNLocale =
    allowedPatientCountries && allowedPatientCountries.length > 1
      ? (allowedPatientCountries.find(apc => apc.isDefault)?.code ?? 'FIN')
      : 'FIN';

  return (
    <>
      <FormRow title="myService.taskListTitle">
        <InputHandler
          name="title"
          type="SelectByCategory"
          editing={!!editing}
          options={titleOptions}
          optionFormatter={o =>
            platformSettings && Object.keys(platformSettings).length > 0
              ? `${platformSettings?.[o]?.titleLocalized[locale] ?? platformSettings?.[o]?.titleLocalized['fi']}`
              : fm(`myService.opts.${o}`)
          }
          formData={{
            document: { title: taskListData?.title },
            onChange: onChangeTaskForm,
          }}
          placeholder="myService.taskListTitlePlaceholder"
        />

        {!taskListData?.title ? (
          <div style={{ marginTop: '2rem', fontWeight: 600 }}>{fm('myService.firstChooseTitle')}</div>
        ) : undefined}
      </FormRow>

      {/** NOTICE: Only show the title field if not selected */}

      {taskListData && taskListData.title && platformSettings?.[taskListData.title]?.titleDescLocalized?.[locale] && (
        <FormRow>
          {platformSettings?.[taskListData.title]?.titleDescLocalized?.[locale] ??
            platformSettings?.[taskListData.title]?.titleDescLocalized?.['fi']}
        </FormRow>
      )}
      <FormRow
        title={'myService.patientNotMysq'}
        condition={
          !!(
            !patientMySQStatus &&
            sentInvites.length === 0 &&
            editing?.type === 'taskList' &&
            !firstInviteUpcoming &&
            taskListData?.title &&
            !showInformantFields
          )
        }
        description={fm('myService.patientNotMysqInfo')}
      >
        <div>
          <div style={{ marginBottom: '0.5rem' }}>{fm('profile.phoneNumbers')}</div>
          <Container>
            <Item xs={5}>
              <InputHandler
                type="SingleValueList"
                name="smsList"
                editing={!!editing}
                value={inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value)}
                sortMethod={(a, b) => {
                  const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                  const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                  return (
                    (aRank || aRank === 0 ? aRank : Number.MAX_SAFE_INTEGER) -
                    (bRank || bRank === 0 ? bRank : Number.MAX_SAFE_INTEGER)
                  );
                }}
                formData={{
                  onChange: onChangeInviteContactInfo,
                  document: {
                    smsList: inviteContactInfo?.filter(ci => ci.type === 'sms').map(sms => sms.value),
                  },
                }}
                disableSaveButton={value => {
                  if (typeof value === 'string')
                    return value.length === 0 || !phone(value, { country: defaultOrgSSNLocale }).isValid;
                  return false;
                }}
                disabledTooltip={fm('profile.notValidSms')}
                component={props => {
                  return <TextField {...props} type="TextField" placeholder={undefined} />;
                }}
              />
            </Item>
          </Container>
          <div style={{ marginBottom: '0.5rem', marginTop: '2rem' }}>{fm('profile.emails')}</div>
          <Container>
            <Item xs={5}>
              <InputHandler
                type="SingleValueList"
                name="emailList"
                editing={!!editing}
                value={inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value)}
                sortMethod={(a, b) => {
                  const aRank = inviteContactInfo?.find(ci => ci.value === a)?.rank;
                  const bRank = inviteContactInfo?.find(ci => ci.value === b)?.rank;
                  return (
                    (aRank || aRank === 0 ? aRank : Number.MAX_SAFE_INTEGER) -
                    (bRank || bRank === 0 ? bRank : Number.MAX_SAFE_INTEGER)
                  );
                }}
                formData={{
                  onChange: onChangeInviteContactInfo,
                  document: {
                    emailList: inviteContactInfo?.filter(ci => ci.type === 'email').map(email => email.value),
                  },
                }}
                disableSaveButton={value => {
                  if (typeof value === 'string') return value.length === 0 || !validate(value);
                  return false;
                }}
                disabledTooltip={fm('profile.notValidEmail')}
                component={props => {
                  return <TextField {...props} type="TextField" placeholder={undefined} />;
                }}
              />
            </Item>
          </Container>
        </div>
      </FormRow>
      <FormRow title="delegates.informants" condition={showInformantFields}>
        <div style={{ maxWidth: '70%' }}>
          <InputHandler
            name="informantId"
            type="Select"
            options={patientInformants.map(rela => rela.id)}
            editing={!!editing}
            formData={{
              document: { informantId: taskListData?.informantId },
              onChange: onChangeTaskForm,
            }}
            optionFormatter={o => {
              if (typeof o === 'string' && !!parsePatientDemographics(defaultOrgSSNLocale, { ssn: o })) {
                const person = taskListData?.newInformant;
                return `${person?.lastName} ${person?.firstName}`;
              } else {
                const person = patientInformants.find(r => r.id === o);
                return `${person?.lastNames} ${person?.firstNames}`;
              }
            }}
            placeholder={'delegates.informants'}
          />
          {editing && !addingNewInformant && (
            <div style={{ marginTop: '1rem' }}>
              <StyledButton onClick={() => setAddingNewInformant(true)}>{`+ ${fm('general.new')}`}</StyledButton>
            </div>
          )}
          {addingNewInformant && (
            <div
              style={{ marginTop: '1rem', backgroundColor: colors.lightGray, padding: '1rem', borderRadius: '1rem' }}
            >
              <RelativeAddingRow title={fm('general.ssn')}>
                <InputHandler
                  name="ssn"
                  type="TextField"
                  editing={!!editing}
                  placeholder={'general.ssn'}
                  formData={{
                    document: { ssn: tempInformantData.ssn },
                    onChange: async (values: TOnChangeValues) => {
                      const value = Object.values(values)?.[0];
                      if (typeof value === 'string') {
                        const isValidField = !!parsePatientDemographics(defaultOrgSSNLocale, { ssn: value });
                        setValidFields(prev => ({ ...prev, ssn: isValidField }));
                        setTempInformantData(prev => ({ ...prev, ssn: value }));
                        if (isValidField && value !== tempInformantData.ssn) {
                          await handleMysqUserDataFetching(value, defaultOrgSSNLocale);
                        } else if (!isValidField && mysqUserDataFound) {
                          setMysqUserDataFound(false);
                          setTempInformantData({
                            ...initialTempInformantData,
                            ssn: value,
                          });
                        }
                      }
                    },
                  }}
                  invalidValue={!validFields.ssn}
                  invalidText={fm('general.checkSsn')}
                />
              </RelativeAddingRow>
              <RelativeAddingRow title={fm('general.lastNames')}>
                <InputHandler
                  name="lastName"
                  type="TextField"
                  editing={!!editing}
                  placeholder={'general.lastNames'}
                  formData={{
                    document: { lastName: tempInformantData.lastName },
                    onChange: (values: TOnChangeValues) => {
                      const value = Object.values(values)?.[0];
                      if (typeof value === 'string') {
                        setTempInformantData(prev => ({ ...prev, lastName: value }));
                      }
                    },
                  }}
                  disabled={mysqUserDataFound}
                />
              </RelativeAddingRow>
              <RelativeAddingRow title={fm('general.firstNames')}>
                <InputHandler
                  name="firstName"
                  type="TextField"
                  editing={!!editing}
                  placeholder={'general.firstNames'}
                  formData={{
                    document: { firstName: tempInformantData.firstName },
                    onChange: (values: TOnChangeValues) => {
                      const value = Object.values(values)?.[0];
                      if (typeof value === 'string') {
                        setTempInformantData(prev => ({ ...prev, firstName: value }));
                      }
                    },
                  }}
                  disabled={mysqUserDataFound}
                />
              </RelativeAddingRow>
              <RelativeAddingRow title={fm('profile.phoneNumber')}>
                <InputHandler
                  name="phoneNumber"
                  type="TextField"
                  editing={!!editing}
                  placeholder={'profile.phoneNumber'}
                  formData={{
                    document: { phoneNumber: tempInformantData.phoneNumber },
                    onChange: (values: TOnChangeValues) => {
                      const value = Object.values(values)?.[0];
                      if (typeof value === 'string') {
                        const parsedPhone = phone(value, { country: defaultOrgSSNLocale });
                        setValidFields(prev => ({ ...prev, phone: value === '' ? true : parsedPhone.isValid }));
                        setTempInformantData(prev => ({ ...prev, phoneNumber: value }));
                      }
                    },
                  }}
                  invalidValue={!validFields.phone && (tempInformantData.phoneNumber?.length ?? 0) > 0}
                  invalidText={fm('delegates.checkPhone')}
                  disabled={mysqUserDataFound}
                />
              </RelativeAddingRow>
              <RelativeAddingRow title={fm('profile.email')}>
                <InputHandler
                  name="email"
                  type="TextField"
                  editing={!!editing}
                  placeholder={'profile.email'}
                  formData={{
                    document: { email: tempInformantData.email },
                    onChange: (values: TOnChangeValues) => {
                      const value = Object.values(values)?.[0];
                      if (typeof value === 'string') {
                        setValidFields(prev => ({ ...prev, email: value === '' ? true : validate(value) }));
                        setTempInformantData(prev => ({ ...prev, email: value }));
                      }
                    },
                  }}
                  invalidValue={!validFields.email && (tempInformantData.email?.length ?? 0) > 0}
                  invalidText={fm('delegates.checkEmail')}
                  disabled={mysqUserDataFound}
                />
              </RelativeAddingRow>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '2rem' }}>
                <ActionButtonRounded
                  text="general.cancel"
                  onClick={() => {
                    setAddingNewInformant(false);
                    setTempInformantData(initialTempInformantData);
                    setValidFields({ ssn: true, email: true, phone: true });
                    setMysqUserDataFound(false);
                  }}
                  width={9.6}
                  height={3}
                  fontSize={16}
                  uppercase={false}
                  loading={loading}
                />
                <ActionButtonRounded
                  text={'general.add'}
                  onClick={() => {
                    setAddingNewInformant(false);
                    onChangeTaskForm([
                      { newInformant: tempInformantData },
                      { informantId: tempInformantData.ssn }, // Adding ssn of the newly added as no id generated yet
                    ]);
                    setTempInformantData(initialTempInformantData);
                    setValidFields({ ssn: true, phone: true, email: true });
                    setMysqUserDataFound(false);
                  }}
                  width={9.6}
                  height={3}
                  fontSize={16}
                  uppercase={false}
                  filled
                  loading={loading}
                  disabled={
                    !Object.values(validFields).every(val => val) ||
                    !(tempInformantData.ssn && (tempInformantData.email || tempInformantData.phoneNumber))
                  }
                />
              </div>
            </div>
          )}
        </div>
      </FormRow>
      <FormRow
        title="myService.linkedAppointment"
        condition={
          !!(
            taskListData?.title &&
            !taskListData?.recurring &&
            (appointments.length > 0 || taskListData?.appointmentId) &&
            (!taskListData?.id || (taskListData?.id && taskListData?.appointmentId)) // If the taskList has been saved already without being linked, dont allow linking
          )
        }
      >
        <InputHandler
          name="appointmentId"
          type="Select"
          editing={!!editing}
          formData={{
            document: { appointmentId: taskListData?.appointmentId },
            onChange: values => {
              const selectedAppointmentId = Object.values(values)[0];
              const selectedAppointment = appointments.find(appo => appo.id === selectedAppointmentId);
              if (!selectedAppointment) return;

              const appointmentDate = selectedAppointment.startDate;
              let specificEndTimeStamp: number | undefined;
              if (selectedAppointment.startTime) {
                specificEndTimeStamp = DateTime.fromMillis(partialDateToValue(appointmentDate))
                  .plus({
                    hours: selectedAppointment.startTime[0],
                  })
                  .plus({ minutes: selectedAppointment.startTime[1] })
                  .toMillis();
              }
              const formChanges = [values, { recurring: undefined }, { deadline: appointmentDate }];
              if (specificEndTimeStamp) {
                formChanges.push({ end_time: specificEndTimeStamp });
              }

              onChangeTaskForm(formChanges);
            },
          }}
          options={appointments.map(appo => appo.id)}
          optionFormatter={o => {
            const appointment = appointments.find(appo => appo.id === o);
            let formatted = '';
            if (appointment?.startDate) {
              formatted += formatPartialDate(appointment?.startDate);
            }
            if (appointment?.startTime) {
              formatted += ` ${formatTime(appointment?.startTime)}`;
            }
            if (appointment?.externalAppointmentType) {
              if (formatted.length > 0) {
                formatted += ', ';
              }
              formatted += appointment.externalAppointmentType;
            }
            return formatted;
          }}
          placeholder={'myService.linkedAppointment'}
        />
      </FormRow>
      <FormRow
        title="myService.recurring"
        condition={platform === 'ninmt' && !!taskListData?.title && !taskListData?.appointmentId}
      >
        <InputHandler
          name="recurring"
          type="CheckboxSingle"
          option="true"
          showLabel={false}
          editing={!!editing}
          disabled={!!(taskListData?.id && taskListData?.recurring)} // Disabled changing recurring if the taskList has already been saved as recurring
          formData={{
            document: { recurring: taskListData?.recurring },
            onChange: () => {
              onChangeTaskForm([{ weeklySurveyInterval: undefined }, { recurring: !taskListData?.recurring }]);
            },
          }}
        />
      </FormRow>
      <FormRow title="myService.recurringInterval" condition={!!taskListData?.recurring}>
        <InputHandler
          name="weeklySurveyInterval"
          type="Select"
          editing={!!editing}
          options={['weekly', 'everyOtherWeek', 'everyFourthWeek']}
          placeholder={'myService.recurringInterval'}
          optionFormatter={o => fm(`myService.opts.${o}`)}
          formData={{
            document: { weeklySurveyInterval: taskListData?.weeklySurveyInterval },
            onChange: onChangeTaskForm,
          }}
        />
      </FormRow>
      <FormRow title="myService.recurringTaskDeadline" condition={!!taskListData?.recurring}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <InputHandler
              name="recurringTaskDeadline"
              type="PartialDate"
              editing={
                !!(
                  editing &&
                  (!taskListData?.id ||
                    (taskListData?.id &&
                      partialDateToValue(taskListData?.recurringTaskDeadline) > partialDateToValue(nowPartialDate())))
                )
              }
              formData={{
                document: { recurringTaskDeadline: taskListData?.recurringTaskDeadline },
                onChange: onChangeTaskForm,
              }}
              dateHook={{ dateHookFloor: nowPartialDate(), dateHookCeiling: taskListData?.deadline }}
            />
          </div>
          {taskListData?.recurringTaskDeadline && (
            <span style={{ paddingBottom: 5 }}>
              {fm(`general.jsDate.${new Date(partialDateToValue(taskListData?.recurringTaskDeadline)).getDay()}`)}
            </span>
          )}
        </div>
      </FormRow>
      <FormRow title={'myService.deadline'} condition={!taskListData?.recurring && !!taskListData?.title}>
        <>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div>
              <InputHandler
                name="deadline"
                type="PartialDate"
                editing={!!(editing && !taskListData?.appointmentId)}
                formData={{ document: { deadline: taskListData?.deadline }, onChange: onChangeTaskForm }}
                dateHook={{ dateHookFloor: nowPartialDate() }}
                isNotCancellable
              />
            </div>
            {showDeadlineTime && (
              <span style={{ marginLeft: '0.5rem', fontWeight: 600 }}>
                {formatTime(appointments.find(appo => appo.id === taskListData?.appointmentId)?.startTime)}
              </span>
            )}
            {taskListData?.deadline && (
              <span style={taskListData?.appointmentId ? { marginLeft: '1rem' } : { paddingBottom: 5 }}>
                {fm(`general.jsDate.${new Date(partialDateToValue(taskListData.deadline)).getDay()}`)}
              </span>
            )}
          </div>
        </>
      </FormRow>
      <FormRow title={'myService.taskDuration'} condition={!!(taskListData?.title && !taskListData?.recurring)}>
        <div>
          {taskListData?.sendDate && taskListData?.deadline && (
            <>
              <StyledDateRange
                from={taskListData.sendDate}
                to={taskListData.deadline}
                controls={{
                  from: (
                    <LeftRightControls
                      action={{
                        right: () => {
                          const currSendDateStamp = partialDateToValue(taskListData.sendDate);
                          const newSendDate = partialDateFromDate(
                            new Date(DateTime.fromMillis(currSendDateStamp).plus({ days: 1 }).toMillis()),
                          );
                          onChangeTaskForm({ sendDate: newSendDate });
                        },
                        left: () => {
                          const currSendDateStamp = partialDateToValue(taskListData.sendDate);
                          const newSendDate = partialDateFromDate(
                            new Date(DateTime.fromMillis(currSendDateStamp).minus({ days: 1 }).toMillis()),
                          );
                          onChangeTaskForm({ sendDate: newSendDate });
                        },
                      }}
                      disabled={{
                        right: partialDateToValue(taskListData.sendDate) >= partialDateToValue(taskListData?.deadline),
                        left: partialDateToValue(taskListData.sendDate) <= partialDateToValue(nowPartialDate()),
                      }}
                    />
                  ),
                }}
              />
            </>
          )}
        </div>
      </FormRow>
      {/** Original sendDate InputHandler, replaced with above custom component. This is only used when recurring is checked. */}
      <FormRow title="myService.sendDate" condition={!!(taskListData?.title && taskListData?.recurring)}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <InputHandler
              name="sendDate"
              type="PartialDate"
              editing={taskListData?.recurring ? false : !!editing}
              formData={{
                document: { sendDate: taskListData?.sendDate },
                onChange: onChangeTaskForm,
              }}
              dateHook={{
                dateHookCeiling: taskListData?.recurringTaskDeadline ?? taskListData?.deadline,
                dateHookFloor: partialDateFromDate(new Date()),
              }}
              isNotCancellable
            />
          </div>
          {taskListData?.sendDate && !taskListData?.recurring && (
            <span style={{ paddingBottom: 5 }}>
              {fm(`general.jsDate.${new Date(partialDateToValue(taskListData?.sendDate)).getDay()}`)}
            </span>
          )}
        </div>
        {taskListData?.sendDate && taskListData?.recurring ? (
          <div>{fm('myService.sendDateRecurringInfo')}</div>
        ) : undefined}
      </FormRow>
      {/** Deadline row second time as fields wanted in different order depending on if recurred checked */}
      <FormRow title={'myService.lastRecurringTaskDeadline'} condition={!!taskListData?.recurring}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div>
            <InputHandler
              name="deadline"
              type="PartialDate"
              editing={!!editing}
              formData={{ document: { deadline: taskListData?.deadline }, onChange: onChangeTaskForm }}
              dateHook={{ dateHookFloor: nowPartialDate() }}
              isNotCancellable
            />
          </div>
          {taskListData?.deadline && (
            <span style={{ paddingBottom: 5 }}>
              {fm(`general.jsDate.${new Date(partialDateToValue(taskListData?.deadline)).getDay()}`)}
            </span>
          )}
        </div>
      </FormRow>
      <FormRow title="myService.tasksForTaskList" condition={!!taskListData?.title}>
        <InputHandler
          name="tasks"
          type="Checkbox"
          editing={!!editing}
          options={platform ? getSurveyOpts(platform, showInformantFields) : []}
          optionFormatter={option =>
            formatTaskActionType({
              fm,
              option,
              recurringDisabledOpts: recurringDisabledOpts as string[],
              latestContraIndicationDocument,
              taskListData,
              platform,
              locale,
              informantVersion: showInformantFields,
            })
          }
          formData={{ document: { tasks: taskListData?.tasks }, onChange: onChangeTaskForm }}
          disabledOptions={
            taskListData?.recurring
              ? [...(disabledOptions ?? []), ...((recurringDisabledOpts as string[]) ?? [])]
              : disabledOptions
          }
        />
        {taskListData?.recurring && recurringDisabledOpts && recurringDisabledOpts.length > 0 ? (
          <div style={{ color: colors.tertiaryText }}>{fm('myService.alreadyRecurring')}</div>
        ) : (
          <></>
        )}
      </FormRow>
      <FormRow title="myService.taskListInfo" condition={!!taskListData?.title}>
        <StyledTextArea
          name="description"
          value={taskListData?.description}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
            onChangeTaskForm({ description: e.currentTarget.value })
          }
          placeholder={fm('myService.taskListInfoPlaceholder')}
          rows={14}
        />
      </FormRow>
    </>
  );
};

interface ITaskListProps {
  inviteContactInfo: (INeuroContactInfo | INeuroUIContactInfo)[] | undefined;
  setInviteContactInfo: React.Dispatch<React.SetStateAction<(INeuroContactInfo | INeuroUIContactInfo)[] | undefined>>;
  onChangeInviteContactInfo: (values: TOnChangeValues) => void;
  locale: string;
  taskListSettings?: TaskListFormProps;
}

export default TaskListForm;
